/* -----------------Globals--------------- */
import { useLocalSearchParams, useRouter } from "expo-router";

import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { pageViewTypes } from "@constants/tracking";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import { useProductDetails } from "@hooks/useProduct";
import { calculatePlanExpiry, getDurationText } from "@memorang/helpers/bundle";
import type { ProductType } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import Loader from "@memorang/ui/components/Loader";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
/* -----------------Child components--------------- */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchExamDetails } from "api/commerce/exam-service";
import { AccessGrantedView } from "components/AccessGrantedView";
import DocumentTitle from "components/DocumentTitle";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
const SuccessRoute = () => {
	const { productType } = useLocalSearchParams<{
		productType: ProductType;
	}>();

	const router = useRouter();

	const {
		duration,
		interval,
		amount,
		currency,
		planId,
		id: productId,
	} = useLocalSearchParams<{
		duration: string;
		interval: string;
		amount: string;
		currency: string;
		planId: string;
		id: string;
	}>();

	const { data: productDetails } = useProductDetails(
		productType,
		Number(productId),
	);

	const examId = productDetails?.examUUID;

	const { data: examDetails, isLoading: isLoadingExamDetails } = useQuery({
		queryKey: [cacheKeys.examDetails, examId],
		queryFn: async () => {
			if (!examId) {
				return undefined;
			}
			return await fetchExamDetails({ examId });
		},
		enabled: !!examId,
	});

	const { explicitCallTrackCustomEvent } = useAnalytics({
		type: pageViewTypes.purchaseSuccess,
	});

	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const headerTitle = t("purchase.success.headerTitle");

	useEffect(() => {
		if (productDetails) {
			const bundleType = productDetails.bundleType;
			explicitCallTrackCustomEvent({
				eventName: events.paymentSuccessful,
				product_id: productDetails.numericId,
				product_name: productDetails.name,
				plan_amount: amount,
				plan_currency: currency,
				plan_id: planId,
				product_type: bundleType,
			});
			queryClient.invalidateQueries();
		}
	}, [
		productDetails,
		queryClient,
		explicitCallTrackCustomEvent,
		amount,
		currency,
		planId,
	]);

	if (!productDetails || isLoadingExamDetails) {
		return (
			<Box flex={1} height="100%" justifyContent="center">
				<CustomAppBar
					title={headerTitle}
					leftIconType="back"
					onPressBack={() => router.replace("/(protected)/(tabs)/home")}
					hideCommandBar
				/>
				<Loader />
			</Box>
		);
	}

	const planExpiry = duration
		? calculatePlanExpiry(interval, Number(duration))
		: undefined;
	const expirationDateText = duration
		? getDurationText(Number(duration), interval)
		: undefined;

	const {
		name,
		examName,
		logicalId: id,
		icon: { url },
		features,
		bundleType,
		bundleSubType,
	} = productDetails;

	const title = t("purchase.success.accessGranted", { name });

	const getDescription = () => {
		if (bundleType === "STUDY_PACK" || bundleSubType === "STUDY_PACK") {
			return t("purchase.success.studyPack");
		}
		if (bundleType === "PRACTICE_TEST" || bundleSubType === "PRACTICE_TEST") {
			return t("purchase.success.practiceTest");
		}

		if (bundleType === "EXAM" || bundleSubType === "EXAM") {
			return t("purchase.success.combo");
		}
	};

	const products = [
		{
			id,
			title: name,
			description: getDescription(),
			icon: url,
			features,
			isCustomAccess: false,
		},
	];
	const successfulPurchaseTitle = `Congratulations! You have unlocked **${expirationDateText}** access to the **${name}**. This will expire on **${planExpiry}**.`;

	return (
		<>
			<CustomAppBar
				title={headerTitle}
				leftIconType="back"
				onPressBack={() => {
					if (isWeb) {
						router.replace("/(protected)/(tabs)/home");
					} else {
						router.back();
					}
				}}
				hideCommandBar
			/>
			<DocumentTitle title={t("purchase.success.documentTitle")} />
			<ScrollContainer maxWidth="xs">
				<Box paddingTop={24} paddingBottom={44}>
					<AccessGrantedView
						products={products}
						examName={examName}
						title={title}
						examDetails={examDetails}
						successfulPurchaseTitle={successfulPurchaseTitle}
					/>
				</Box>
			</ScrollContainer>
		</>
	);
};

export default SuccessRoute;
